import React, { useEffect, useState } from "react";
import {
  Activity,
  Droplets,
  Footprints,
  Moon,
  LucideUtensils,
} from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import CircularProgress from "../../CircularProgress";
import Card from "../../Card/Card";
import Shimmer from "src/components/common/Shimmer";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as actions from "src/redux/slices/configSlice";
import { loggers } from "src/types/config.types";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

interface TodaysProgressProps {
  id: string;
  title: string;
  description: string;
  type: string;
  supportProgramKey: string;
  options: Array<{
    id: string;
    color: string;
    title: string;
  }>;
}

const TodaysProgress: React.FC<TodaysProgressProps> = ({
  id,
  title,
  description,
  type,
  supportProgramKey,
  options,
}) => {
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const { reminders } = useAppSelector((state) => state.config);
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (patient?.data?.patient_id) {
      getReminders();
    }
  }, [patient]);

  useEffect(() => {
    if (options?.length > 0) {
      setData(
        options?.map((option) => {
          return {
            ...option,
            data: reminders?.data?.loggers_data?.[
              accessKeySet[option.id as keyof typeof accessKeySet] as loggers
            ],
          };
        })
      );
    }
  }, [options, reminders?.data?.loggers_data]);

  const getReminders = () => {
    new Promise((resolve, reject) => {
      dispatch(
        actions.getRemindersRequest({
          payload: {
            user_id: patient?.data?.patient_id,
            current_date: moment().format("YYYY-MM-DD"),
          },
          resolve,
          reject,
        })
      );
    });
  };

  const iconSet = {
    "water-logs": <Droplets />,
    "steps-logs": <Footprints />,
    "sleep-logs": <Moon />,
    "diet-logs": <LucideUtensils />,
  };

  const accessKeySet = {
    "water-logs": "water_data",
    "steps-logs": "steps_data",
    "sleep-logs": "sleep_data",
    "diet-logs": "diet_data",
  };

  const navigateToLogDetailsScreen = (loggerId: any) => {
    // Event: TODAYS_PROGRESS_ITEM_CLICKED
    const loggerName = loggerId.replace("-", "_");
    analytics.trackEvent(
      `${loggerName}_${EVENT_NAMES.TODAYS_PROGRESS_ITEM_CLICKED}`,
      {
        screen_name: SCREEN_NAMES.HOME_SCREEN,
        card_title: title,
        support_program_key: supportProgramKey,
      }
    );

    // Navigate to log details screen
    navigate(`/app/${supportProgramKey}/log-details-screen?log=${loggerId}`);
  };

  return (
    <div className="mx-4" id={id}>
      <Card
        title={title}
        icon={<Activity className="w-4 h-4" />}
        description={description}
      >
        <div className="flex justify-center items-start gap-4 px-2">
          {reminders?.isLoading ? (
            <>
              {options?.map((option: any, index: number) => {
                return (
                  <div
                    key={index.toString()}
                    className="flex flex-col items-center gap-2"
                  >
                    <Shimmer
                      width="64px"
                      height="64px"
                      rounded="rounded-full"
                    />
                    <Shimmer width="60px" height="16px" />
                    <Shimmer width="60px" height="16px" />
                  </div>
                );
              })}
            </>
          ) : (
            reminders?.data?.loggers_data &&
            data?.map((option: any) => {
              const key = option.id;
              const goalValue = option.data?.goal_value || 1000;
              const acheivedValue = option.data?.acheived_value || 0;
              const unit = option.data?.unit || "";

              return (
                <button
                  className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigateToLogDetailsScreen(key)}
                >
                  <CircularProgress
                    color={option.color}
                    width={64}
                    height={64}
                    target={goalValue}
                    value={acheivedValue}
                  >
                    {/* add className with option.color */}
                    {/* {iconSet[key as keyof typeof iconSet]} */}
                    {React.cloneElement(iconSet[key as keyof typeof iconSet], {
                      className: `w-4 h-4 text-[${option.color}]`,
                    })}
                  </CircularProgress>
                  <div className="text-xs text-[var(--text-secondary)] text-center font-semibold uppercase tracking-wide">
                    {option.title}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] text-center">
                    {`${acheivedValue} / ${goalValue} ${unit}`}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
};

export default TodaysProgress;
